import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { BrandMark } from "@/components/brand-mark";
import { BrandThemeToggle } from "@/components/layout/brand-theme-toggle";
import { LiveClock } from "@/components/layout/top-bar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useNewLook } from "@/hooks/use-new-look";
import { getInitials } from "@/lib/get-initials";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth-store";

/**
 * The new look's header (docs/new-look/PLAN.md, step 1.3): back on detail
 * pages, the logo, the preview switch, theme and clock on desktop, and the
 * avatar, which opens Settings.
 */
export function NewLookHeader({ isMobile }: { isMobile: boolean }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [, setNewLook] = useNewLook();
  const isDetail = pathname.split("/").filter(Boolean).length > 1;
  const onSettings = pathname === "/settings" || pathname.startsWith("/settings/");

  return (
    <header className="sticky top-0 z-40 border-b border-border/60 bg-background/90 pt-safe backdrop-blur-xl">
      <div className="mx-auto flex h-14 w-full max-w-[1280px] items-center justify-between gap-3 px-4 sm:h-16 sm:px-8">
        <div className="flex min-w-0 items-center gap-1">
          {isDetail && (
            <button
              type="button"
              onClick={() => {
                void navigate(-1);
              }}
              aria-label="Go back"
              className="-ml-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <ArrowLeft className="h-5 w-5" aria-hidden="true" />
            </button>
          )}
          <Link to="/dashboard" aria-label="CompBox home" className="flex min-w-0 items-center">
            <BrandMark withWordmark={!isMobile} />
          </Link>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          {!isMobile && (
            <span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary">
              New look preview
            </span>
          )}
          <button
            type="button"
            onClick={() => {
              setNewLook(false);
            }}
            className="min-h-11 whitespace-nowrap rounded-lg px-2 text-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {isMobile ? "Classic look" : "Back to the classic look"}
          </button>
          {!isMobile && <BrandThemeToggle />}
          {!isMobile && <LiveClock />}
          <button
            type="button"
            onClick={() => {
              void navigate("/settings");
            }}
            aria-label="Settings"
            aria-current={onSettings ? "page" : undefined}
            className={cn(
              "flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              onSettings ? "ring-2 ring-primary/60" : "hover:bg-muted"
            )}
          >
            <Avatar className="h-8 w-8">
              {user?.avatarUrl && <AvatarImage src={user.avatarUrl} alt="" />}
              <AvatarFallback className="bg-muted text-xs">{getInitials(user?.name ?? "")}</AvatarFallback>
            </Avatar>
          </button>
        </div>
      </div>
    </header>
  );
}
